import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Layer, type MapLayerMouseEvent, Popup, Source } from 'react-map-gl';
import { Feature, Geometry } from 'geojson';
import { useTrailPhotos } from '@alltrails/trails';
import type TrailPhoto from '@alltrails/types/TrailPhoto';
import getPhotoUrl from '@alltrails/core/utils/getPhotoUrl';
import { SPACE_50 } from '@alltrails/denali/tokens';
import { useGroupHover } from '@alltrails/core';
import { Lightbox } from '@alltrails/lightbox';
import useMap from '../../hooks/useMap';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import { defaultSymbolLayerLayoutProps, getLayerBeforeId, userPhotosId } from '../../utils/layers';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import { useSelector } from '../../redux';
import userPhotoSvg, { svgSizeWithoutShadow } from '../../utils/svgDefinitions/userPhotoSvg';
import styles from './styles/styles.module.scss';

const svgDefinitions: SvgDefinition[] = [userPhotoSvg];

type FeatureProperties = Pick<TrailPhoto, 'id' | 'photoHash' | 'title'> & {
  index: number;
  latitude: number;
  longitude: number;
};

type UserPhotosProps = { onPhotoLightboxChange?: (isOpen: boolean) => void; trailId: number };

const UserPhotos = ({ onPhotoLightboxChange, trailId }: UserPhotosProps) => {
  const map = useMap();
  const context = useSelector(state => state.context);
  const { lightboxPhotos, photos, loadPhotos } = useTrailPhotos({ trailId });
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const sourceId = 'user-photos';

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const features = useMemo(() => {
    if (!photos?.length) {
      return [];
    }
    return photos.reduce(
      (allPhotos, { id, location, photoHash, title }, index) => {
        if (!location) return allPhotos;

        const { latitude, longitude } = location;
        // Technically this could filter out a valid photo that was taken at exactly 0,0,
        // but the server is sending down 0,0 in prod for photos that don't have location data,
        // so we'd rather filter out that one-in-a-trillion photo rather than show a bunch of photos in the wrong location.
        if (latitude && longitude) {
          allPhotos.push({
            geometry: { type: 'Point', coordinates: [longitude, latitude] },
            properties: { id, index, latitude, longitude, photoHash, title },
            type: 'Feature'
          });
        }
        return allPhotos;
      },
      [] as Feature<Geometry, FeatureProperties>[]
    );
  }, [photos]);

  const [hoveredPhoto, setHoveredPhoto] = useState<FeatureProperties>();

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const onHover = useCallback((photo: FeatureProperties) => {
    setHoveredPhoto(photo);
  }, []);

  const onHoverEnd = useCallback(() => {
    setHoveredPhoto(undefined);
  }, []);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true);

  const onMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      if (onMouseEnter && event.features) {
        const photo = event.features[0].properties as FeatureProperties;
        onMouseEnter(photo);
      }
    },
    [onMouseEnter]
  );

  useEffect(() => {
    registerEventHandler(userPhotosId, 'mousemove', onMouseMove);
    onMouseLeave && registerEventHandler(userPhotosId, 'mouseleave', onMouseLeave);
    return () => {
      unregisterEventHandler(userPhotosId, 'mousemove', onMouseMove);
      onMouseLeave && unregisterEventHandler(userPhotosId, 'mouseleave', onMouseLeave);
    };
  }, [onMouseLeave, onMouseEnter, registerEventHandler, unregisterEventHandler, onMouseMove]);

  const popup = useMemo(() => {
    if (!hoveredPhoto) {
      return null;
    }

    const { id, latitude, longitude, photoHash, title } = hoveredPhoto;
    return (
      <Popup
        closeButton={false}
        closeOnClick={false}
        focusAfterOpen={false}
        latitude={latitude}
        longitude={longitude}
        maxWidth="none"
        offset={svgSizeWithoutShadow / 2 + SPACE_50}
      >
        <button
          className={styles.popup}
          onClick={() => {
            onPhotoLightboxChange?.(true);
            setLightboxIndex(hoveredPhoto.index);
          }}
          onMouseEnter={() => onMouseEnter?.(hoveredPhoto)}
          onMouseLeave={onMouseLeave}
        >
          <img alt={title} src={getPhotoUrl({ photo: { id, photoHash }, size: '160x160' })} />
        </button>
      </Popup>
    );
  }, [hoveredPhoto, onMouseEnter, onMouseLeave, onPhotoLightboxChange]);

  const lightbox = useMemo(() => {
    if (lightboxIndex === -1) {
      return null;
    }
    return (
      <Lightbox
        closeModal={() => {
          onPhotoLightboxChange?.(false);
          setLightboxIndex(-1);
        }}
        context={context}
        languageRegionCode={context.languageRegionCode}
        photos={lightboxPhotos}
        startingIndex={lightboxIndex}
        trailId={trailId}
      />
    );
  }, [context, lightboxIndex, lightboxPhotos, onPhotoLightboxChange, trailId]);

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  if (!imagesAreLoaded) {
    return null;
  }

  return (
    <>
      <Source id={sourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
        <Layer
          id={userPhotosId}
          source={sourceId}
          beforeId={getLayerBeforeId(map, userPhotosId)}
          type="symbol"
          layout={{ ...defaultSymbolLayerLayoutProps, 'icon-allow-overlap': true, 'icon-image': userPhotoSvg.name }}
        />
      </Source>
      {popup}
      {lightbox}
    </>
  );
};

export default UserPhotos;
